
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, Pencil, X, Check, FileText, FileStack, ChevronDown,
  Send, Trophy, XCircle, Undo2, AlertTriangle, ShoppingCart, Mail, BookOpen,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useGenerateOfferNumber } from "@/hooks/useOfferNumber";
import { Estimate, LostReason } from '@/types';
import ExportDropdown from './ExportDropdown';
import EstimateStatusBadge from './EstimateStatusBadge';
import EstimateRevisionsDropdown from './EstimateRevisionsDropdown';
import ParentEstimateBadge from './ParentEstimateBadge';
import PriceStaleBadge from './PriceStaleBadge';
import EstimateBreadcrumb from './EstimateBreadcrumb';
import { EstimateStratigraphy } from '@/types/estimateStratigraphy';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import { useEstimates } from '@/hooks/useEstimates';
import EstimateNotesField from "./EstimateNotesField";
import { useEstimateNotes } from "@/hooks/useEstimateNotes";
import { useOrgProfile } from "@/hooks/useOrgSettings";
import { useMaterialsSummary } from "@/hooks/useMaterialsSummary";
import { Sentry } from "@/lib/sentry";
import { isEstimateLocked } from '@/utils/estimates/estimateLock';
import { useHasFeature } from '@/hooks/useOrgPlan';
import { Crown, AlertOctagon } from 'lucide-react';
import { useCanWrite } from '@/hooks/useCanWrite';

interface EstimateHeaderProps {
  estimate: Estimate;
  stratigraphies: (EstimateStratigraphy & { stratigraphy?: any })[];
}

const LOST_REASON_OPTIONS: { value: LostReason; label: string; hint: string }[] = [
  { value: 'price', label: 'Prezzo troppo alto', hint: 'Il cliente ha trovato offerte piu` economiche' },
  { value: 'timing', label: 'Tempi non compatibili', hint: 'Non si è riusciti a rispettare le tempistiche richieste' },
  { value: 'competitor', label: 'Concorrente scelto', hint: 'Il cliente ha preferito un altro fornitore' },
  { value: 'no_response', label: 'Cliente non ha risposto', hint: 'Nessun feedback dopo l\'invio dell\'offerta' },
  { value: 'other', label: 'Altro motivo', hint: 'Motivo non classificabile o specifico del singolo cliente' },
];

const EstimateHeader = ({ estimate, stratigraphies }: EstimateHeaderProps) => {
  const navigate = useNavigate();
  const { updateEstimateStatus, isUpdatingStatus, updateLostReason, isUpdatingLostReason } = useEstimates();
  const [isEditing, setIsEditing] = useState(false);
  const [tmpName, setTmpName] = useState(estimate.name);
  const [isSaving, setIsSaving] = useState(false);
  const [isExportingRDA, setIsExportingRDA] = useState(false);
  const [lostDialogOpen, setLostDialogOpen] = useState(false);
  const [lostReason, setLostReason] = useState<LostReason>('price');
  /**
   * F30 bonus: distingue "Segna come perso" (transizione → setta lost_at +
   * lost_reason) da "Modifica motivo" (solo lost_reason, niente timestamp).
   */
  const [lostDialogMode, setLostDialogMode] = useState<'transition' | 'edit'>('transition');
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const { data: orgProfile } = useOrgProfile();
  const generateOfferNumber = useGenerateOfferNumber();
  const hasTechnicalSheet = useHasFeature('technicalSheet');
  const { canWrite } = useCanWrite();
  const hasPdfBranding = useHasFeature('pdfBranding');
  // Solo passa orgProfile (logo + dati intestazione) quando il piano lo include.
  // Su Solo: null → gli export renderizzano header DryConfig di default.
  const orgForPdf = hasPdfBranding ? (orgProfile ?? null) : null;
  // F31: usato dall'export "Ordine al fornitore". Stessa fonte del riepilogo
  // materiali a video → coerenza prezzi/quantità garantita.
  const { materialsSummary } = useMaterialsSummary(stratigraphies);

  const status = estimate.status;
  const locked = isEstimateLocked(status);

  // Carica customer (via project) per il bottone "Apri email" sul preventivo
  // inviato. Query leggera, abilitata solo se status puo' usare mailto.
  const canEmail = status === 'sent' || status === 'won';
  const { data: customerEmailInfo } = useQuery<{ email: string | null; name: string }>({
    queryKey: ['estimate-customer-email', estimate.id],
    enabled: canEmail && !!estimate.projectId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from('projects')
        .select('customer_id')
        .eq('id', estimate.projectId)
        .maybeSingle();
      const customerId = (data as { customer_id?: string | null } | null)?.customer_id;
      if (!customerId) return { email: null, name: '' };
      const { data: c } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('customers' as any)
        .select('email, name')
        .eq('id', customerId)
        .maybeSingle();
      return {
        email: (c as { email?: string | null } | null)?.email ?? null,
        name: (c as { name?: string } | null)?.name ?? '',
      };
    },
  });

  const handleOpenEmail = () => {
    if (!customerEmailInfo?.email) {
      toast({ title: 'Cliente senza email registrata — aggiungila nell\'anagrafica' });
      return;
    }
    const offerLabel = estimate.offerNumber || estimate.name || 'Preventivo';
    const subject = `Preventivo ${offerLabel}`;
    const greeting = customerEmailInfo.name ? `Buongiorno ${customerEmailInfo.name},` : 'Buongiorno,';
    const body =
      `${greeting}%0D%0A%0D%0Ain allegato troverà il preventivo richiesto (${offerLabel}).` +
      `%0D%0AResto a disposizione per chiarimenti.%0D%0A%0D%0ACordiali saluti`;
    const mailto = `mailto:${encodeURIComponent(customerEmailInfo.email)}?subject=${encodeURIComponent(subject)}&body=${body}`;
    window.location.href = mailto;
  };

  // Funzione per aggiornare solo il nome del preventivo
  const handleSaveName = async () => {
    if (tmpName.trim() === '') {
      toast({ title: 'Il nome non può essere vuoto' });
      return;
    }
    setIsSaving(true);
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const { error } = await supabase
        .from('estimates')
        .update({ name: tmpName })
        .eq('id', estimate.id);

      if (error) throw error;
      toast({ title: 'Nome preventivo aggiornato!' });
      setIsEditing(false);
      window.location.reload();
    } catch (err: any) {
      toast({ title: 'Errore nel salvataggio del nome' });
    }
    setIsSaving(false);
  };

  // Gestione notes
  const {
    saveNotes, isSaving: isSavingNotes,
    saveInternalNotes, isSavingInternal,
  } = useEstimateNotes(estimate.id);

  /**
   * Genera (o legge) il numero offerta e poi esegue l'export.
   * Variant:
   *  - 'sintetico'        → preventivo cliente
   *  - 'completo'         → preventivo cantiere/tecnico
   *  - 'ordine_fornitori' → lista materiali raggruppata per supplier (F31)
   *
   * L'ordine al fornitore non ha bisogno del numero offerta (è interno) ma
   * lo riportiamo se gia` presente per tracciabilita`.
   */
  const handleExportOffer = async (
    variant: 'sintetico' | 'completo' | 'ordine_fornitori' | 'capitolato',
  ) => {
    if (isExportingRDA) return;
    setIsExportingRDA(true);
    try {
      const needsOfferNumber = variant !== 'ordine_fornitori';
      let offerNumber = estimate.offerNumber as string | null | undefined;
      let issuedAt = estimate.offerIssuedAt as Date | null | undefined;
      if (needsOfferNumber && !offerNumber) {
        const info = await generateOfferNumber.mutateAsync(estimate.id);
        offerNumber = info.offer_number;
        issuedAt = new Date();
      }
      const enrichedEstimate = {
        ...estimate,
        offerNumber: offerNumber ?? null,
        offerIssuedAt: issuedAt ?? new Date(),
      };

      const exp = await import('@/utils/export');
      if (variant === 'sintetico') {
        await exp.exportSyntheticOffer({
          estimate: enrichedEstimate,
          stratigraphies,
          org: orgForPdf,
        });
      } else if (variant === 'completo') {
        await exp.exportCompleteRDA(enrichedEstimate, stratigraphies, orgForPdf);
      } else if (variant === 'capitolato') {
        await exp.exportCapitolato({
          estimate: enrichedEstimate,
          stratigraphies,
          org: orgForPdf,
        });
      } else {
        await exp.exportSupplierOrder({
          estimate: enrichedEstimate,
          materials: materialsSummary,
          org: orgForPdf,
        });
      }
      const labels: Record<typeof variant, string> = {
        sintetico: 'Preventivo Sintetico',
        completo: 'Preventivo Completo',
        capitolato: 'Capitolato Tecnico',
        ordine_fornitori: 'Ordine ai fornitori',
      };
      toast({
        title: `${labels[variant]} generato`,
        description: offerNumber && needsOfferNumber
          ? `Numero offerta: ${offerNumber}`
          : 'Documento scaricato',
      });
    } catch (error) {
      console.error('Errore export preventivo:', error);
      Sentry.captureException(error, {
        tags: { feature: 'export-offer', variant },
        extra: {
          estimateId: estimate?.id,
          stratigraphyCount: stratigraphies?.length,
        },
      });
      // Gli errori Supabase (PostgrestError) NON sono istanze di Error: sono
      // oggetti { message, details, hint, code }. Senza questo String(error)
      // dava "[object Object]". Estraggo il message anche dagli oggetti.
      const errMsg =
        error instanceof Error
          ? error.message
          : error && typeof error === 'object' && 'message' in error
            ? String((error as { message: unknown }).message)
            : String(error);
      toast({
        title: 'Errore generazione documento',
        description: errMsg.slice(0, 200),
        variant: 'destructive',
      });
    } finally {
      setIsExportingRDA(false);
    }
  };

  /**
   * F30 — workflow transitions. Non c'è una macchina a stati rigida: vogliamo
   * permettere agli utenti di correggere errori (es. ho marcato come "vinto"
   * un preventivo sbagliato → riporto in bozza). Quindi consentiamo qualunque
   * transizione, salvo richiedere il motivo quando si va in `lost`.
   */
  const goSent = () => updateEstimateStatus(estimate.id, 'sent');
  const goWon = () => updateEstimateStatus(estimate.id, 'won');
  const goLostConfirm = () => {
    if (lostDialogMode === 'edit') {
      // F30 bonus: aggiorna solo lost_reason, niente nuovo lost_at.
      updateLostReason(estimate.id, lostReason);
    } else {
      // Transizione vera in lost: setta lost_at + lost_reason in atomico.
      updateEstimateStatus(estimate.id, 'lost', lostReason);
    }
    setLostDialogOpen(false);
  };
  const openEditLostReason = () => {
    // Precarica il motivo attuale (se presente) nel dialog.
    const current = (estimate.lostReason as LostReason | null) ?? 'price';
    setLostReason(current);
    setLostDialogMode('edit');
    setLostDialogOpen(true);
  };
  const openLostTransition = () => {
    setLostReason('price');
    setLostDialogMode('transition');
    setLostDialogOpen(true);
  };
  const reopen = () => {
    updateEstimateStatus(estimate.id, 'draft');
    setReopenDialogOpen(false);
  };

  return (
    <div className="mb-8">
      <EstimateBreadcrumb
        projectId={estimate.projectId}
        projectName={estimate.projectName}
        estimateName={estimate.name}
      />
      <div className="flex gap-2">
        <Button
          onClick={() => navigate(`/projects/${estimate.projectId}`)}
          variant="ghost"
          size="sm"
          className="mb-4 -ml-2 text-muted-foreground"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Torna al Progetto
        </Button>
      </div>
      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-6">
        <div className="flex flex-col gap-1 w-full max-w-2xl">
          <div className="flex items-center gap-3">
            {isEditing ? (
              <>
                <Input
                  value={tmpName}
                  onChange={(e) => setTmpName(e.target.value)}
                  className="max-w-xs"
                  disabled={isSaving}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleSaveName();
                    if (e.key === 'Escape') {
                      setIsEditing(false);
                      setTmpName(estimate.name);
                    }
                  }}
                  autoFocus
                />
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={handleSaveName}
                  disabled={isSaving}
                  aria-label="Salva"
                >
                  <Check className="w-4 h-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => {
                    setIsEditing(false);
                    setTmpName(estimate.name);
                  }}
                  disabled={isSaving}
                  aria-label="Annulla"
                >
                  <X className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <h1 className="text-3xl font-bold tracking-tight">
                  {estimate.name}
                </h1>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setIsEditing(true)}
                  aria-label="Modifica nome">
                  <Pencil className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
          {/* Versioning: se è una revisione, mostra link al padre */}
          {estimate.parentEstimateId && (
            <div className="mt-1">
              <ParentEstimateBadge
                parentEstimateId={estimate.parentEstimateId}
                currentVersion={estimate.version}
              />
            </div>
          )}
          <p className="text-muted-foreground mt-1">
            {estimate.description || 'Gestisci le stratigrafie e i costi del preventivo'}
          </p>
        </div>
        <div className="flex flex-col items-end gap-3 flex-shrink-0">
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <EstimateStatusBadge
              status={status}
              createdAt={estimate.createdAt}
              sentAt={estimate.sentAt}
              wonAt={estimate.wonAt}
              lostAt={estimate.lostAt}
            />
            <PriceStaleBadge estimateId={estimate.id} />

            {/* F30 — Workflow buttons */}
            {(status === 'draft' || status === 'pending') && (
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={goSent}
                disabled={isUpdatingStatus || !canWrite}
              >
                <Send className="h-4 w-4" />
                Marca come inviato
              </Button>
            )}
            {(status === 'sent' || status === 'pending') && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={handleOpenEmail}
                  title={customerEmailInfo?.email ? `Apri email a ${customerEmailInfo.email}` : 'Cliente senza email registrata'}
                >
                  <Mail className="h-4 w-4" />
                  Apri email
                </Button>
                <Button
                  size="sm"
                  variant="default"
                  className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={goWon}
                  disabled={isUpdatingStatus || !canWrite}
                >
                  <Trophy className="h-4 w-4" />
                  Cliente accettato
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 border-red-300 text-red-700 hover:bg-red-50"
                  onClick={openLostTransition}
                  disabled={isUpdatingStatus || !canWrite}
                >
                  <XCircle className="h-4 w-4" />
                  Segna come perso
                </Button>
              </>
            )}
            {/* F30 bonus: modifica solo il motivo di un preventivo già perso,
                senza ricreare il timestamp lost_at. */}
            {status === 'lost' && (
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5 border-red-300 text-red-700 hover:bg-red-50"
                onClick={openEditLostReason}
                disabled={isUpdatingStatus || isUpdatingLostReason}
              >
                <Pencil className="h-4 w-4" />
                Modifica motivo
              </Button>
            )}
            {locked && (
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={() => setReopenDialogOpen(true)}
                disabled={isUpdatingStatus || !canWrite}
              >
                <Undo2 className="h-4 w-4" />
                Riapri (torna in bozza)
              </Button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="default"
                  className="gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={isExportingRDA}
                >
                  <FileStack className="h-4 w-4" />
                  {isExportingRDA ? 'Generando...' : 'Genera preventivo'}
                  <ChevronDown className="h-4 w-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel>Scegli il formato del preventivo</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleExportOffer('sintetico')}>
                  <FileText className="h-4 w-4 mr-2 text-primary" />
                  <div className="flex flex-col">
                    <span className="font-medium">Preventivo Sintetico</span>
                    <span className="text-xs text-muted-foreground">
                      Per il cliente · 2-3 pagine · prezzi finali + IVA
                    </span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExportOffer('completo')}>
                  <FileStack className="h-4 w-4 mr-2 text-primary" />
                  <div className="flex flex-col">
                    <span className="font-medium">Preventivo Completo</span>
                    <span className="text-xs text-muted-foreground">
                      Per il cantiere · composizione + acquisti + tutto
                    </span>
                  </div>
                </DropdownMenuItem>
                {/* Capitolato: priorità canWrite (subscription) > hasTechnicalSheet (gate piano).
                    cancelled  → badge rosso "Riattiva", click → billing
                    Solo       → badge giallo "Studio", click → billing
                    Studio/Team → export normale */}
                <DropdownMenuItem
                  onClick={() => {
                    if (!canWrite || !hasTechnicalSheet) {
                      navigate('/settings/billing');
                      return;
                    }
                    handleExportOffer('capitolato');
                  }}
                  className={(!canWrite || !hasTechnicalSheet) ? 'opacity-70' : ''}
                >
                  <BookOpen className={`h-4 w-4 mr-2 ${canWrite && hasTechnicalSheet ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                  <div className="flex flex-col flex-1">
                    <span className="font-medium flex items-center gap-1.5">
                      Capitolato Tecnico
                      {!canWrite ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                          <AlertOctagon className="h-2.5 w-2.5" />
                          Riattiva
                        </span>
                      ) : !hasTechnicalSheet ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                          <Crown className="h-2.5 w-2.5" />
                          Studio
                        </span>
                      ) : null}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {!canWrite
                        ? 'Abbonamento scaduto — clicca per riattivare'
                        : !hasTechnicalSheet
                          ? 'Disponibile da Studio — clicca per passare al piano superiore'
                          : 'Per gare e Direzione Lavori · voci normative + condizioni'}
                    </span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleExportOffer('ordine_fornitori')}>
                  <ShoppingCart className="h-4 w-4 mr-2 text-orange-600" />
                  <div className="flex flex-col">
                    <span className="font-medium">Ordine ai fornitori</span>
                    <span className="text-xs text-muted-foreground">
                      Da inviare al rappresentante · raggruppato per fornitore
                    </span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <EstimateRevisionsDropdown estimate={estimate} stratigraphies={stratigraphies} />
            <ExportDropdown estimate={estimate} stratigraphies={stratigraphies} />
          </div>
        </div>
      </div>

      {/* Note preventivo (PDF cliente) + Note interne (solo team) — affiancate a tutta larghezza */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <EstimateNotesField
          note={estimate.notes}
          onSave={async (text) => { await saveNotes(text); }}
          disabled={isSavingNotes}
        />
        <EstimateNotesField
          variant="internal"
          note={estimate.internalNotes}
          onSave={async (text) => { await saveInternalNotes(text); }}
          disabled={isSavingInternal}
        />
      </div>

      {/* Dialog: motivo perdita (transition o edit) */}
      <Dialog open={lostDialogOpen} onOpenChange={setLostDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              {lostDialogMode === 'edit' ? 'Modifica motivo perdita' : 'Segna preventivo come perso'}
            </DialogTitle>
            <DialogDescription>
              {lostDialogMode === 'edit'
                ? 'Aggiorna il motivo registrato. La data di perdita resta invariata.'
                : 'Registra il motivo della perdita: ti aiuta a tracciare i pattern di vendita nella dashboard e capire dove migliorare. Puoi sempre riaprire il preventivo in seguito.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="text-sm font-medium">Motivo</label>
            <Select value={lostReason} onValueChange={(v) => setLostReason(v as LostReason)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOST_REASON_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <div className="flex flex-col">
                      <span>{opt.label}</span>
                      <span className="text-xs text-muted-foreground">{opt.hint}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setLostDialogOpen(false)}
              disabled={isUpdatingStatus || isUpdatingLostReason}
            >
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={goLostConfirm}
              disabled={isUpdatingStatus || isUpdatingLostReason}
            >
              {lostDialogMode === 'edit' ? 'Salva motivo' : 'Conferma perdita'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert: riapri preventivo chiuso */}
      <AlertDialog open={reopenDialogOpen} onOpenChange={setReopenDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Riaprire il preventivo?</AlertDialogTitle>
            <AlertDialogDescription>
              Il preventivo tornerà in <strong>Bozza</strong> e potrai di nuovo modificarne
              stratigrafie e prezzi. I timestamp di "vinto"/"perso" verranno cancellati e dovrai
              ri-registrarli quando concluderai di nuovo la trattativa. Il numero offerta e
              la data di emissione restano invariati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUpdatingStatus || !canWrite}>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={reopen} disabled={isUpdatingStatus || !canWrite}>
              Riapri in bozza
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EstimateHeader;
